<template>
  <div>
    <v-form>
      <v-text-field
        v-if="context !== 'create'"
        v-model="localReport.name"
        :label="$tc('name')"
        :error-messages="nameErrors"
        prepend-inner-icon="mdi-form-textbox"/>
      <div class="text-subtitle-1 mt-4">{{ $tc('date_range') }}</div>
      <div class="d-flex align-center">
        <date-picker
          v-model="startDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('start_date')"
          prepend-inner-icon="mdi-calendar"
          :error-messages="dateRangeErrors"
          :reset-on-timezone-change="context === 'create'"/>
        <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
        <date-picker
          v-model="endDate"
          :timezone="timezone"
          :output-moment="true"
          :end-of-day="true"
          :label="$t('end_date')"
          prepend-inner-icon="mdi-calendar"
          :error="dateRangeErrors.length > 0"
          :reset-on-timezone-change="context === 'create'"/>
      </div>
      <date-selector-shortcuts :lowerBound.sync="startDate" :upperBound.sync="endDate" :timezone="timezone"/>
      <hr class="mt-4 mb-2">
      <div class="text-subtitle-1 mt-4">{{ $t('comparison' )}}</div>
      <div class="d-flex align-center">
        <date-picker
          v-model="comparisonStartDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('start_date')"
          prepend-inner-icon="mdi-calendar"
          :error-messages="comparisonDateRangeErrors"
          :reset-on-timezone-change="context === 'create'"/>
        <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
        <date-picker
          v-model="comparisonEndDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('end_date')"
          prepend-inner-icon="mdi-calendar"
          :error="comparisonDateRangeErrors.length > 0"
          :reset-on-timezone-change="context === 'create'"/>
      </div>
      <date-selector-shortcuts :lowerBound.sync="comparisonStartDate" :upperBound.sync="comparisonEndDate" :timezone="timezone"/>
    </v-form>
  </div>
</template>

<script>
import DatePicker from '@/components/shared/DatePicker'
import DateSelectorShortcuts from '@/components/report/DateSelectorShortcuts'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import ReportUtils from '@/components/report/ReportUtils'
import { requiredIf } from 'vuelidate/lib/validators'
import { get } from 'lodash'

export default {
  name: 'RootCauseReportForm',
  components: {
    DatePicker,
    DateSelectorShortcuts
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin,
    ValidationMixin,
    ReportFormMixin
  ],
  validations: {
    localReport: {
      name: {
        required: requiredIf(function () {
          return this.action !== 'create'
        })
      }
    },
    dateRange: {
      rangeIsValid: function () {
        return ReportUtils.dateRangeIsValid(this.startDate, this.endDate)
      }
    },
    comparisonDateRange: {
      rangeIsValid: function (value) {
        // Comparison is optional. Valid if both values are empty
        if (this.comparisonStartDate == null && this.comparisonEndDate == null) {
          return true
        }
        // Otherwise, similar logic to dateRange.rangeIsValid
        return ReportUtils.dateRangeIsValid(this.comparisonStartDate, this.comparisonEndDate)
      }
    }
  },
  data () {
    return {
      reportType: 'RootCauseReport',
      startDate: null,
      endDate: null,
      comparisonStartDate: null,
      comparisonEndDate: null
    }
  },
  computed: {
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.localReport.name,
        message: this.$t('required')
      }])
    },
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    },
    comparisonDateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.comparisonDateRange,
        message: this.$t('invalid_date_range')
      }])
    }
  },
  methods: {
    defaultReportName () {
      return this.$t('reports_ui.root_cause_report.name')
    },
    /** Hook to populate report (called from mixin fns) */
    populateReportForm () {
      this.preSelectDates()
      this.preSelectComparisonDates()
    },
    /** Build report params (called from mixin) */
    buildReportParameters () {
      const reportParameters = {}
      reportParameters.startDate = this.startDate.format()
      reportParameters.endDate = this.endDate.format()
      if (this.comparisonStartDate && this.comparisonEndDate) {
        reportParameters.comparisonStartDate = this.comparisonStartDate.format()
        reportParameters.comparisonEndDate = this.comparisonEndDate.format()
      }
      reportParameters.customerId = get(this, 'localReport.parameters.customerId') || this.customerContext.id
      reportParameters.timezone = this.timezone
      return reportParameters
    }
  }
}
</script>

<style scoped>

</style>
